import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import PageMasthead from '../../components/PageMasthead'
import ModelSelect from '../../components/classifier/ModelSelect'
import { FIELD_CLASS, LABEL_CLASS } from '../../components/inputs/fieldStyles'
import {
    Modality,
    Valence,
    useClassifierNameAvailabilityQuery,
    useCreateClassifierDetailMutation,
    useCreateClassifierPromptMutation,
    useDeleteClassifierDetailMutation
} from '../../api/client'
import { NAME_MAX_LENGTH, SHORT_DESCRIPTION_MAX_LENGTH } from './classifierFieldLimits'
import ClassifierMetadataFields, {
    ClassifierMetadata,
    isMetadataOverLimit
} from './classifier/ClassifierMetadataFields'
import {
    DEFAULT_BORDERLINE_THRESHOLD,
    DEFAULT_CONFIRMED_THRESHOLD,
    thresholdError
} from './classifier/classifierVersions'
import ThresholdHelp from './classifier/ThresholdHelp'
import { DEFAULT_LLM_MODEL } from '../../models/LlmModel'
import { errorMessage } from '../../util/errorMessage'

// Slugify the display name into the immutable shortName the backend requires.
function slugify(s: string): string {
    return s
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
}

// Long enough that we're not querying on every keystroke, short enough that the
// verdict lands before the admin has moved on to the next field.
const AVAILABILITY_DEBOUNCE_MS = 400

export default function CreateClassifierPage() {
    const navigate = useNavigate()

    const [metadata, setMetadata] = useState<ClassifierMetadata>({
        displayName: '',
        clientFacingName: '',
        shortDescription: '',
        modality: Modality.Text,
        // Negative by default: almost every classifier anyone creates here is one
        // that should moderate. The column default is neutral for a different
        // reason — a row inserted by hand without an opinion must not silently
        // start moderating. — Claude
        valence: Valence.Negative
    })
    const [model, setModel] = useState(DEFAULT_LLM_MODEL)
    const [prompt, setPrompt] = useState('')
    // Nullable, like the draft editor: clearing a threshold means the first version
    // doesn't define that band, which is not the same as a 0 that would sweep every
    // comment into the queue.
    const [borderline, setBorderline] = useState<number | null>(DEFAULT_BORDERLINE_THRESHOLD)
    const [confirmed, setConfirmed] = useState<number | null>(DEFAULT_CONFIRMED_THRESHOLD)
    const [error, setError] = useState<string | null>(null)

    const [createDetail, { loading: creatingDetail }] = useCreateClassifierDetailMutation()
    const [createPrompt, { loading: creatingPrompt }] = useCreateClassifierPromptMutation()
    const [deleteDetail] = useDeleteClassifierDetailMutation()

    const submitting = creatingDetail || creatingPrompt

    // shortName is derived, immutable and unique, but never shown — so a collision has
    // to be reported against the Name field the admin can actually see.
    const shortName = slugify(metadata.displayName)
    const nameHasNoSlug = metadata.displayName.trim().length > 0 && shortName === ''

    const [checkedShortName, setCheckedShortName] = useState('')
    useEffect(() => {
        const timeout = setTimeout(() => setCheckedShortName(shortName), AVAILABILITY_DEBOUNCE_MS)
        return () => clearTimeout(timeout)
    }, [shortName])

    const { data: availability, loading: checkingName } = useClassifierNameAvailabilityQuery({
        variables: { shortName: checkedShortName },
        skip: checkedShortName === ''
    })

    // Only trust the answer once it's caught up with what's in the field, so a stale
    // verdict never sits under a name the admin has already changed.
    const nameChecked = checkedShortName === shortName && !checkingName
    const conflict = nameChecked ? availability?.classifierNameAvailability : undefined
    const nameTaken = conflict?.available === false
    const conflictingName = conflict?.conflictingDisplayName

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        setError(null)

        const thresholdsInvalid = thresholdError(borderline, confirmed)
        if (thresholdsInvalid) {
            setError(thresholdsInvalid)
            return
        }
        if (
            metadata.displayName.length > NAME_MAX_LENGTH ||
            metadata.clientFacingName.length > NAME_MAX_LENGTH
        ) {
            setError(`Names must be ${NAME_MAX_LENGTH} characters or fewer.`)
            return
        }
        if (metadata.shortDescription.length > SHORT_DESCRIPTION_MAX_LENGTH) {
            setError(`The description must be ${SHORT_DESCRIPTION_MAX_LENGTH} characters or fewer.`)
            return
        }
        if (nameHasNoSlug) {
            setError('The name must contain at least one letter or number.')
            return
        }

        // 1. Create the classifier definition.
        let detailId: number
        try {
            const res = await createDetail({
                variables: {
                    input: {
                        shortName,
                        displayName: metadata.displayName.trim(),
                        clientFacingName: metadata.clientFacingName.trim(),
                        shortDescription: metadata.shortDescription.trim(),
                        // longDescription is a legacy column nothing reads any more —
                        // shortDescription is the single source of truth, so leave it empty
                        // rather than seeding a duplicate that goes stale on edit.
                        longDescription: '',
                        modality: metadata.modality,
                        valence: metadata.valence
                    }
                }
            })
            const created = res.data?.createClassifierDetail
            if (!created) throw new Error('No classifier returned')
            detailId = created.id
        } catch (err) {
            setError(`Couldn't create the classifier: ${errorMessage(err)}`)
            return
        }

        // 2. Create the first draft version. Roll back the definition on failure so
        //    we never leave a promptless (and therefore invisible) classifier behind.
        try {
            await createPrompt({
                variables: {
                    input: {
                        classifierDetailsId: detailId,
                        prompt: prompt.trim(),
                        model,
                        defaultBorderlineThreshold: borderline,
                        defaultConfirmedThreshold: confirmed
                    }
                }
            })
        } catch (err) {
            await deleteDetail({ variables: { id: detailId } }).catch(() => {})
            setError(`Couldn't create the first version: ${errorMessage(err)}`)
            return
        }

        navigate(`/admin/classifiers/${detailId}/edit`)
    }

    return (
        <div className="min-h-screen bg-ink-50">
            <PageMasthead title="New classifier" tabTitle="New classifier" />

            <form onSubmit={handleSubmit} className="max-w-2xl space-y-6 p-6">
                {error && (
                    <div className="rounded-lg border border-severe/30 bg-severe/10 p-3 text-sm text-severe">
                        {error}
                    </div>
                )}

                {/* Details — the unversioned metadata */}
                <div className="space-y-4 rounded-lg border border-ink-200 bg-white p-4">
                    <h2 className="text-sm font-semibold text-ink-900">Details</h2>
                    <ClassifierMetadataFields
                        value={metadata}
                        onChange={setMetadata}
                        overLimitAction="continue"
                        required
                        nameIssues={
                            <>
                                {nameHasNoSlug && (
                                    <p className="mt-1 text-xs text-severe">
                                        The name must contain at least one letter or number.
                                    </p>
                                )}
                                {nameTaken && (
                                    <p className="mt-1 text-xs text-severe">
                                        {conflictingName
                                            ? `That name is already used by the classifier “${conflictingName}”. Choose a different one.`
                                            : 'That name is already used by another classifier. Choose a different one.'}
                                    </p>
                                )}
                            </>
                        }
                    />
                </div>

                {/* First version — the versioned prompt/model/thresholds */}
                <div className="space-y-4 rounded-lg border border-arwen-200 border-l-4 border-l-arwen-600 bg-white p-4">
                    <h2 className="text-sm font-semibold text-arwen-700">First draft version</h2>
                    <div className="flex flex-wrap gap-4">
                        <ModelSelect value={model} onChange={setModel} />
                        {/* Thresholds are deliberately uncapped: a signal-only classifier such as
                          Buyer Intent is stopped from moderating anything by setting them above 1
                          (SPAM uses 1.01), so no score can ever cross them. Only the ordering
                          rule in thresholdError applies, and ThresholdHelp explains the rest. */}
                        <div className="flex items-end gap-3">
                            <div>
                                <label htmlFor="borderline" className={LABEL_CLASS}>
                                    Borderline threshold
                                </label>
                                <input
                                    id="borderline"
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    placeholder="None"
                                    value={borderline ?? ''}
                                    onChange={(e) =>
                                        setBorderline(
                                            e.target.value === '' ? null : Number(e.target.value)
                                        )
                                    }
                                    className={`${FIELD_CLASS} w-28 tabular-nums`}
                                />
                            </div>
                            <div>
                                <label htmlFor="confirmed" className={LABEL_CLASS}>
                                    Confirmed threshold
                                </label>
                                <input
                                    id="confirmed"
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    placeholder="None"
                                    value={confirmed ?? ''}
                                    onChange={(e) =>
                                        setConfirmed(
                                            e.target.value === '' ? null : Number(e.target.value)
                                        )
                                    }
                                    className={`${FIELD_CLASS} w-28 tabular-nums`}
                                />
                            </div>
                            {/* Sits level with the inputs rather than the labels, so the row
                                reads as one control group. */}
                            <ThresholdHelp className="mb-2.5" />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="prompt" className={LABEL_CLASS}>
                            Prompt{' '}
                            <span className="normal-case text-ink-400 font-normal">
                                — internal, never shown to clients
                            </span>
                        </label>
                        <textarea
                            id="prompt"
                            required
                            rows={10}
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            className={`${FIELD_CLASS} font-mono text-xs`}
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2">
                    <Link
                        to="/admin/classifiers"
                        className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50"
                    >
                        Cancel
                    </Link>
                    <button
                        type="submit"
                        disabled={
                            submitting ||
                            isMetadataOverLimit(metadata) ||
                            nameHasNoSlug ||
                            nameTaken
                        }
                        className="rounded-lg bg-arwen-600 px-4 py-2 text-sm font-bold text-white hover:bg-arwen-700 disabled:opacity-50"
                    >
                        {submitting ? 'Creating…' : 'Create classifier'}
                    </button>
                </div>
            </form>
        </div>
    )
}
